import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, Download, ExternalLink, FilePen, Loader, Pencil, X } from "lucide-react";
import { lazy, Suspense, useEffect, useRef, useState } from "react";

import type { DocxEditor } from "@upclaim/docx-editor";

import { BoxContentPreview } from "@/components/files/box-content-preview";
import { formatFileSize } from "@/components/files/file-card";
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { downloadFromBoxFileId } from "@/features/claims/api/use-claim-actions";
import { openBoxFile } from "@/features/claims/lib/open-box";
import { prefetchFileDocx } from "@/features/docx-editor/api/use-file-docx";
import { DocxDownloadButton } from "@/features/docx-editor/components/docx-download-button";
import { DocxViewerSkeleton } from "@/features/docx-editor/components/docx-viewer-skeleton";
import { baseName, DOCX_MIME } from "@/features/docx-editor/lib/download";
import { FileItemIcon } from "@/features/file-explorer/components/item-icon";
import { PageSkeleton, PdfViewerSkeleton } from "@/features/pdf-editor/components/pdf-viewer-skeleton";
import { useCurrentUser } from "@/hooks/use-current-user";
import { notify } from "@/lib/notify";
import { DOCUMENT_EDITOR_ROLES } from "@/lib/permissions";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import adminAPI from "@/services/api";

export interface PreviewFile {
  id: string;
  name: string | null;
  mimeType: string | null;
  size: number | null;
  /** For local File objects (created via URL.createObjectURL) — skips the signed-URL fetch. */
  previewUrl?: string;
  /** When present, shows the « Ouvrir dans Box » button (files/FilePreviewModal.vue). */
  boxFileId?: string | null;
}

interface FilePreviewDialogProps {
  open: boolean;
  file: PreviewFile | null;
  onClose: () => void;
}

/** Our PDF viewer (pdf.js), loaded with the first stored PDF previewed: not in the main bundle. */
const PdfPreview = lazy(() => import("@/features/pdf-editor/components/pdf-preview"));

/** Our Word viewer, loaded with the first stored .docx previewed. */
const DocxPreview = lazy(loadDocxViewer);

/** Our viewers fill the dialog, edge to edge. */
const QUICK_LOOK = "h-full rounded-none border-0";

/** Fallback when the browser has no idle callback (Safari). */
const PRELOAD_DELAY_MS = 2000;

/** The Word viewer's code (editor engine, fonts): in its own chunk, out of the main bundle. */
function loadDocxViewer() {
  return import("@/features/docx-editor/components/docx-preview");
}

/**
 * Pages that can preview files load the viewers — pdf.js (code and worker) and the Word one — once the browser is
 * idle (cached afterwards): the first document opened doesn't wait for them.
 */
function usePreloadViewers() {
  useEffect(() => {
    const preload = () => {
      void import("@/features/pdf-editor/lib/pdf-sessions").then((module) => module.preloadPdfViewer());
      void loadDocxViewer();
    };
    if ("requestIdleCallback" in window) {
      const handle = window.requestIdleCallback(preload, { timeout: 5000 });
      return () => window.cancelIdleCallback(handle);
    }
    const timer = setTimeout(preload, PRELOAD_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);
}

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "avif", "heic", "heif"];

const WORD_EXTENSIONS = ["doc", "docx"];
const WORD_MIME_TYPES = ["application/msword", DOCX_MIME];

/** Lowercased extension, or "" when the name has none. */
function extensionOf(name: string | null | undefined): string {
  if (!name || !name.includes(".")) {
    return "";
  }
  return name.split(".").pop()?.toLowerCase() ?? "";
}

function isPdfFile(file: PreviewFile) {
  return file.mimeType === "application/pdf" || extensionOf(file.name) === "pdf";
}

function isImageFile(file: PreviewFile) {
  return (file.mimeType?.startsWith("image/") ?? false) || IMAGE_EXTENSIONS.includes(extensionOf(file.name));
}

function isWordFile(file: PreviewFile) {
  return WORD_MIME_TYPES.includes(file.mimeType ?? "") || WORD_EXTENSIONS.includes(extensionOf(file.name));
}

/** Starts opening a stored PDF in our viewer (pdf.js stays out of the main bundle until then). */
function prefetchPdf(fileId: string) {
  void import("@/features/pdf-editor/lib/pdf-sessions").then((module) => module.prefetchPdfSession(fileId));
}

/** A stored .docx, which opens in our Word viewer (a legacy .doc keeps Box's). */
function isDocxFile(file: PreviewFile) {
  return isWordFile(file) && extensionOf(file.name) === "docx";
}

/** Starts loading a stored .docx and the viewer's code, so the viewer finds both on their way. */
function prefetchDocx(queryClient: QueryClient, fileId: string) {
  void loadDocxViewer();
  prefetchFileDocx(queryClient, fileId);
}

/**
 * Starts what the preview of `file` will need — the PDF session, an image's signed URL, a Word document — so the
 * dialog opens on a document already on its way (`usePreviewPrefetch`: a pointer resting on it).
 */
export function prefetchFilePreview(queryClient: QueryClient, file: PreviewFile) {
  if (file.previewUrl) {
    return;
  }
  if (isPdfFile(file)) {
    prefetchPdf(file.id);
  } else if (isImageFile(file)) {
    void queryClient.prefetchQuery({
      queryKey: queryKeys.files.signedUrl(file.id),
      queryFn: () => adminAPI.retrieveFile(file.id),
    });
  } else if (isDocxFile(file)) {
    prefetchDocx(queryClient, file.id);
  } else if (isWordFile(file) && file.boxFileId !== null) {
    void queryClient.prefetchQuery({
      queryKey: queryKeys.files.boxPreviewToken(file.id),
      queryFn: () => adminAPI.getFilePreviewToken(file.id),
    });
  }
}

/**
 * Port of modals/FilePreviewModal.vue — image/PDF preview with download fallback.
 * Single preview primitive for every file surface (documents, expenses, notes, exhibits…).
 */
export function FilePreviewDialog({ open, file, onClose }: FilePreviewDialogProps) {
  usePreloadViewers();
  const queryClient = useQueryClient();
  const { can } = useCurrentUser();
  // The stored mimeType is often null, so fall back to the filename extension —
  // parity with ClaimDocuments.vue (lookup(file.name)); lets .png/.jpg preview.
  const extension = extensionOf(file?.name);
  const isPdf = file?.mimeType === "application/pdf" || extension === "pdf";
  const isImage = (file?.mimeType?.startsWith("image/") ?? false) || IMAGE_EXTENSIONS.includes(extension);
  const isWordDoc = WORD_MIME_TYPES.includes(file?.mimeType ?? "") || WORD_EXTENSIONS.includes(extension);
  // Word documents render through Box Content Preview (local File objects can't — they're not in Box).
  const usesBoxPreview = isWordDoc && !file?.previewUrl;
  // Stored PDFs open in our viewer, read by ranges; local File objects keep the browser's.
  const usesPdfViewer = isPdf && !file?.previewUrl;
  // Online editing of stored files (not local File objects): .docx (not legacy .doc) and PDF,
  // for the roles the API lets save.
  const editorRoute =
    usesBoxPreview && extension === "docx"
      ? "/documents/$fileId/edit"
      : isPdf && !file?.previewUrl
        ? "/documents/$fileId/pdf"
        : null;
  const canEditOnline = can(DOCUMENT_EDITOR_ROLES) && editorRoute !== null;
  // Stored .docx open in our Word viewer for the roles the editor's API serves; other roles and legacy .doc
  // keep Box's preview.
  const usesDocxViewer = usesBoxPreview && extension === "docx" && can(DOCUMENT_EDITOR_ROLES);
  /** The document in our Word viewer, once loaded: « Télécharger » offers it as PDF. */
  const [docxEditor, setDocxEditor] = useState<DocxEditor | null>(null);

  // The dialog opens at once, light; the viewer mounts when the animation is done. Meanwhile the PDF is
  // already loading (prefetched on hover, or from this click).
  const [isOpenSettled, setIsOpenSettled] = useState(false);
  useEffect(() => {
    if (open && file && usesPdfViewer) {
      prefetchPdf(file.id);
    } else if (open && file && usesDocxViewer) {
      prefetchDocx(queryClient, file.id);
    }
  }, [open, file, usesPdfViewer, usesDocxViewer, queryClient]);
  // Edits made in the Word viewer: closing asks first.
  const isDocxDirty = useRef(false);
  const [isConfirmingClose, setIsConfirmingClose] = useState(false);

  const { data: signedUrl, isPending } = useQuery({
    queryKey: queryKeys.files.signedUrl(file?.id ?? ""),
    queryFn: () => adminAPI.retrieveFile(file?.id as string),
    enabled: open && !!file && !file.previewUrl && !usesBoxPreview && !usesPdfViewer,
  });
  const { data: boxPreview, isPending: isBoxPreviewPending } = useQuery({
    queryKey: queryKeys.files.boxPreviewToken(file?.id ?? ""),
    queryFn: () => adminAPI.getFilePreviewToken(file?.id as string),
    enabled: open && !!file && usesBoxPreview && !usesDocxViewer,
  });

  // Renaming (files/FilePreviewModal.vue): pencil → inline input. `renamed` keeps the new
  // name displayed after save — the `file` prop is a parent snapshot that won't refetch live.
  const [draftName, setDraftName] = useState<string | null>(null);
  const [renamed, setRenamed] = useState<{ id: string; name: string } | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const displayName = (renamed?.id === file?.id ? renamed?.name : null) ?? file?.name;

  const renameFile = useMutation({
    mutationFn: (name: string) =>
      notify.promise(adminAPI.renameFile(file?.id as string, { name }), {
        loading: "Renommage du document...",
        success: "Document renommé",
        error: "Erreur lors du renommage",
      }),
    onSuccess: async ({ success }, name) => {
      if (success && file) {
        setRenamed({ id: file.id, name });
        setDraftName(null);
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.all });
      }
    },
  });

  function saveRename() {
    const name = draftName?.trim();
    if (name && name !== displayName) {
      renameFile.mutate(name);
    } else {
      setDraftName(null);
    }
  }

  function handleClose({ force = false } = {}) {
    if (isDocxDirty.current && !force) {
      setIsConfirmingClose(true);
      return;
    }
    isDocxDirty.current = false;
    setIsConfirmingClose(false);
    setDraftName(null);
    onClose();
  }

  const fileUrl = file?.previewUrl ?? signedUrl ?? "";
  const isLoading =
    !file?.previewUrl &&
    !usesPdfViewer &&
    !usesDocxViewer &&
    (usesBoxPreview ? isBoxPreviewPending : isPending);

  async function downloadFile() {
    if (!file) {
      return;
    }
    if (file.previewUrl) {
      const anchor = document.createElement("a");
      anchor.href = file.previewUrl;
      anchor.download = file.name ?? "download";
      anchor.click();
      return;
    }
    setIsDownloading(true);
    try {
      // Blob download of the cross-origin S3 file (the claim-documents bucket CORS
      // allows the app origin) — a plain anchor's download attr is ignored cross-origin.
      await downloadFromBoxFileId(file.id, file.name ?? "download");
    } catch (error) {
      notify.error("Erreur lors du téléchargement", {
        description: error instanceof Error ? error.message : "Une erreur est survenue",
      });
    } finally {
      setIsDownloading(false);
    }
  }

  const meta = [extension.toUpperCase(), file?.size ? formatFileSize(file.size) : null]
    .filter(Boolean)
    .join(" · ");
  const fullHeight = "flex h-full items-center justify-center";

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => !nextOpen && handleClose()}
      onOpenChangeComplete={setIsOpenSettled}
    >
      {/* Tall and edge to edge: the document is what the dialog is for. */}
      <DialogContent
        showCloseButton={false}
        className="h-[90vh] gap-0 overflow-hidden p-0 sm:max-w-5xl lg:w-4/6 lg:max-w-none"
      >
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-gray-200/70 px-4">
          {file ? (
            <FileItemIcon file={{ name: file.name ?? "", mimeType: file.mimeType }} className="h-8 w-6" />
          ) : null}
          {draftName !== null ? (
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <Input
                value={draftName}
                autoFocus
                size="sm"
                aria-label="Nouveau nom"
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    saveRename();
                  } else if (e.key === "Escape") {
                    // Cancels the rename only, not the dialog.
                    e.stopPropagation();
                    setDraftName(null);
                  }
                }}
              />
              <Button size="sm" onClick={saveRename} disabled={renameFile.isPending}>
                {renameFile.isPending ? <Loader className="animate-spin" /> : <Check />}
                Enregistrer
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Annuler le renommage"
                onClick={() => setDraftName(null)}
                disabled={renameFile.isPending}
              >
                <X />
              </Button>
            </div>
          ) : (
            <div className="group flex min-w-0 flex-1 items-center gap-1">
              <div className="min-w-0">
                <DialogTitle className="truncate text-base font-semibold text-gray-900">
                  {displayName}
                </DialogTitle>
                {meta ? <p className="text-xs text-gray-500 tabular-nums">{meta}</p> : null}
              </div>
              {/* A previewUrl file is local (object URL): nothing stored to rename. */}
              {file?.previewUrl ? null : (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0 text-gray-400 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                  title="Renommer le document"
                  aria-label="Renommer le document"
                  onClick={() => setDraftName(displayName ?? "")}
                >
                  <Pencil />
                </Button>
              )}
            </div>
          )}
          <div className="flex shrink-0 items-center gap-2">
            {canEditOnline && editorRoute && file ? (
              <Button
                variant="outline"
                render={<Link to={editorRoute} params={{ fileId: file.id }} preload="render" />}
              >
                <FilePen />
                Modifier
              </Button>
            ) : null}
            {usesDocxViewer && file ? (
              <DocxDownloadButton
                onDownload={downloadFile}
                onDownloadPdf={
                  docxEditor
                    ? () => void docxEditor.print(baseName(displayName ?? "document"), { final: true })
                    : undefined
                }
                disabled={isDownloading}
              />
            ) : (
              <Button variant="outline" onClick={downloadFile} disabled={isDownloading}>
                {isDownloading ? <Loader className="animate-spin" /> : <Download />}
                Télécharger
              </Button>
            )}
            {file?.boxFileId ? (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Ouvrir dans Box"
                title="Ouvrir dans Box"
                onClick={() => openBoxFile(file.boxFileId as string)}
              >
                <ExternalLink />
              </Button>
            ) : null}
            <DialogClose render={<Button variant="ghost" size="icon" aria-label="Fermer" />}>
              <X />
            </DialogClose>
          </div>
        </header>

        <div className="min-h-0 flex-1 bg-gray-50">
          {isLoading ? (
            <div className={fullHeight}>
              <Spinner className="size-6" />
            </div>
          ) : usesPdfViewer && !isOpenSettled ? (
            // The viewers (pdf.js, Box's) mount after the opening animation, never during it: meanwhile, their
            // skeleton — the same toolbar and page — so nothing jumps when they arrive.
            <PdfViewerSkeleton className={QUICK_LOOK} />
          ) : usesDocxViewer && !isOpenSettled ? (
            <DocxViewerSkeleton className={QUICK_LOOK} />
          ) : usesDocxViewer && file ? (
            <Suspense fallback={<DocxViewerSkeleton className={QUICK_LOOK} />}>
              <DocxPreview
                fileId={file.id}
                className={QUICK_LOOK}
                onDirtyChange={(dirty) => {
                  isDocxDirty.current = dirty;
                }}
                onEditorChange={setDocxEditor}
              />
            </Suspense>
          ) : usesBoxPreview && !isOpenSettled ? (
            <div className="h-full overflow-hidden pt-6">
              <PageSkeleton />
            </div>
          ) : usesBoxPreview && boxPreview ? (
            <BoxContentPreview
              fileId={boxPreview.fileId}
              accessToken={boxPreview.accessToken}
              className="h-full rounded-none"
            />
          ) : usesPdfViewer && file ? (
            <Suspense fallback={<PdfViewerSkeleton className={QUICK_LOOK} />}>
              {/* A quick look: the page at 100%, no pages panel (the file page opens at 125% with it). */}
              <PdfPreview fileId={file.id} className={QUICK_LOOK} initialZoom={1} defaultPagesOpen={false} />
            </Suspense>
          ) : isPdf && fileUrl ? (
            <iframe src={fileUrl} title={file?.name ?? "Aperçu"} className="h-full w-full" />
          ) : isImage && fileUrl ? (
            <div className={cn(fullHeight, "p-6")}>
              <img
                src={fileUrl}
                alt={file?.name ?? "Preview"}
                className="max-h-full max-w-full rounded-md object-contain shadow-sm ring-1 ring-black/5"
              />
            </div>
          ) : (
            <div className={cn(fullHeight, "flex-col text-center")}>
              <h3 className="text-sm font-medium text-gray-900">Aperçu non disponible</h3>
              <p className="mt-1 text-sm text-gray-500">
                Ce type de fichier ne peut pas être prévisualisé. Vous pouvez le télécharger.
              </p>
              <Button className="mt-4" onClick={downloadFile} disabled={isDownloading}>
                {isDownloading ? <Loader className="animate-spin" /> : <Download />}
                Télécharger le fichier
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
      <AlertDialog open={isConfirmingClose} onOpenChange={setIsConfirmingClose}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Fermer sans enregistrer ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vos modifications de « {displayName} » seront perdues.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setIsConfirmingClose(false)}>
              Continuer l'édition
            </Button>
            <Button variant="destructive" onClick={() => handleClose({ force: true })}>
              Fermer sans enregistrer
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Dialog>
  );
}
